import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import { Button, Flex, Hug, Logo, spacing, Text } from '@cash-design-system/react'

const SEVENTY_FIVE_PERCENT_OPACITY = 'BF'

const NotificationPreviewWrapper = styled.div`
  height: 12rem;
  background: ${props => props.theme.colors.formFieldBackground};
  border-radius: 0.75rem;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  overflow: hidden;
`

const Phone = styled.div`
  width: 80%;
  min-width: 18rem;
  max-width: 24rem;
  height: 10rem;
  border-radius: 2.5rem 2.5rem 0 0;
  border: 0.75rem solid #000;
  border-bottom-style: none;
  padding: ${({ theme }) => theme.spacing.s};
  overflow: hidden;
  background: #000;

  @keyframes turn-on {
    to {
      background: ${props => props.theme.colors.green};
    }
  }

  animation-timing-function: ease;
  animation-duration: 300ms;
  animation-delay: 300ms;
  animation-name: turn-on;
  animation-iteration-count: 1;
  animation-fill-mode: forwards;
`

const Notification = styled(Flex)`
  background: ${props => `${props.theme.colors.background}${SEVENTY_FIVE_PERCENT_OPACITY}`};
  border-radius: 1rem;
  padding: ${({ theme }) => theme.spacing.s};
  align-items: flex-start;
  column-gap: ${spacing.s};
  transform: translateY(-125%);
  opacity: 0;

  @keyframes slide-down {
    to {
      opacity: 1;
      transform: translateY(0%);
    }
  }

  animation-timing-function: ease;
  animation-duration: 350ms;
  animation-delay: 500ms;
  animation-name: slide-down;
  animation-iteration-count: 1;
  animation-fill-mode: forwards;
`

const LogoWrapper = styled.div`
  background: white;
  border-radius: 0.5rem;
  padding: ${({ theme }) => theme.spacing.empty};
  width: 1.75rem;
  height: 1.75rem;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xxs};
`

// This mimics a push notification on a phone, so not design system related
const Title = styled(Text)`
  color: ${props => props.theme.colors.label};
  font-weight: ${props => props.theme.weights.medium}; /* stylelint-disable-line @cash-web/no-hardcoded-font-styles */
`

// This mimics a push notification on a phone, so not design system related
const Subtitle = styled(Text)`
  color: ${props => props.theme.colors.label};
  line-height: 1.25rem; /* stylelint-disable-line @cash-web/no-hardcoded-font-styles */
  margin-bottom: 0.125rem;
`

type PushNotificationModalProps = {
  notificationBody: string
  notificationHeader: string
  sendNotification: () => void
  subtitle: string
}
export const PushNotificationModal = ({
  notificationBody,
  notificationHeader,
  sendNotification,
  subtitle,
}: PushNotificationModalProps) => {
  const { t } = useTranslation()
  const { maxWidths } = useTheme()
  return (
    <Hug maxWidth={maxWidths.content}>
      <Flex gap={spacing.xxl} flexDirection="column">
        <Flex gap={spacing.m} flexDirection="column">
          <Text typography="numeralSmall" as="h2" color="textStandard">
            {t('Continue in the app', { context: 'header' })}
          </Text>
          <Text typography="body">{subtitle}</Text>
        </Flex>
        <NotificationPreviewWrapper>
          <Phone>
            <Notification data-testid="push-notification">
              <LogoWrapper>
                <Logo width="1.75rem" height="1.75rem" />
              </LogoWrapper>
              <Content>
                <Title typography="helpText">{notificationHeader}</Title>
                <Subtitle typography="helpText">{notificationBody}</Subtitle>
              </Content>
            </Notification>
          </Phone>
        </NotificationPreviewWrapper>
        <Button size="large" variant="secondary" onClick={sendNotification}>
          {t('Send notification again', { context: 'cta button text' })}
        </Button>
      </Flex>
    </Hug>
  )
}
